/**
 * events_details.js — Alpine.js component + AOS cleanup for the event-details widget.
 * Auto-bundled by Vite's import.meta.glob — do NOT import manually.
 *
 * Data contract (emitted by events_details.jinja):
 *   <script type="application/json" data-event-details-payload>{ ...event object... }</script>
 */

document.addEventListener('alpine:init', () => {
    const payloadEl = document.querySelector('[data-event-details-payload]');
    const initialPayload = payloadEl ? JSON.parse(payloadEl.textContent) : {};

    Alpine.data('eventDetails', () => ({
        showGallery: false,
        currentGalleryIndex: 0,
        eventPayload: initialPayload,
        galleryImages: initialPayload.gallery || [],
        eventDescriptionHtml: initialPayload.description || '<p>Event description goes here...</p>',
        startDateLabel: '',
        endDateLabel: '',
        timeRangeLabel: '',
        messages: {
            linkCopied: 'Link copied to clipboard!',
            allowPopupsToPrint: 'Please allow popups for this site to print PDFs',
            copied: 'Copied!',
            copyFailedManual: 'Copy failed. Please copy manually:',
            unableToCopyManual: 'Unable to copy to clipboard. Please copy the URL manually:',
            galleryImage: 'Gallery image',
            shareOptions: 'Share options',
            shareOnX: 'Share on X/Twitter',
            shareOnFacebook: 'Share on Facebook',
            shareOnLinkedIn: 'Share on LinkedIn',
            shareOnPinterest: 'Share on Pinterest',
            copyLinkLabel: 'Copy link',
            printPdfLabel: 'Print PDF',
            viewPdfLabel: 'View PDF',
            imageGalleryLabel: 'Image gallery',
            closeGalleryLabel: 'Close gallery',
            previousImageLabel: 'Previous image',
            nextImageLabel: 'Next image'
        },

        init() {
            this.refreshDateLabels();
            window.addEventListener('language-switched', () => {
                this.refreshDateLabels();
            });
        },

        getActiveLocale() {
            return document.documentElement.lang || navigator.language || 'en-US';
        },

        formatDate(dateStr, locale = this.getActiveLocale()) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return new Intl.DateTimeFormat(locale, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }).format(date);
        },

        formatTime(dateStr, locale = this.getActiveLocale()) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return new Intl.DateTimeFormat(locale, {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            }).format(date);
        },

        refreshDateLabels() {
            const startDate = this.eventPayload.start_date || '';
            const endDate = this.eventPayload.end_date || '';
            const startTime = this.formatTime(startDate);
            const endTime = this.formatTime(endDate);

            this.startDateLabel = this.formatDate(startDate);
            this.endDateLabel = this.formatDate(endDate);
            this.timeRangeLabel = [startTime, endTime].filter(Boolean).join(' - ');
        },

        openGallery(index) {
            this.currentGalleryIndex = index;
            this.showGallery = true;
            document.body.style.overflow = 'hidden';
        },

        closeGallery() {
            this.showGallery = false;
            document.body.style.overflow = '';
        },

        nextImage() {
            this.currentGalleryIndex = (this.currentGalleryIndex + 1) % this.galleryImages.length;
        },

        prevImage() {
            this.currentGalleryIndex = (this.currentGalleryIndex - 1 + this.galleryImages.length) % this.galleryImages.length;
        },

        addToGoogleCalendar() {
            const event = this.eventPayload;
            const start = new Date(event.start_date).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
            const end = new Date(event.end_date).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
            const details = event.short_description || '';
            const location = event.venue ? `${event.venue.name}, ${event.venue.address}, ${event.venue.city}` : '';

            const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${start}/${end}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;
            window.open(url, '_blank');
        },

        addToAppleCalendar() {
            const event = this.eventPayload;

            const formatICSDate = (dateStr) => {
                if (!dateStr) return '';
                return dateStr.replace(' ', 'T').replace(/[-:]/g, '').substring(0, 15);
            };

            const start = formatICSDate(event.start_date);
            const end = formatICSDate(event.end_date);
            const title = (event.title || 'Event').replace(/,/g, '\\,');
            const description = (event.short_description || '').replace(/\n/g, '\\n').replace(/,/g, '\\,');
            const location = event.venue
                ? [event.venue.name, event.venue.address, event.venue.city, event.venue.zip].filter(Boolean).join(', ').replace(/,/g, '\\,')
                : '';
            const uid = `${event.id || Date.now()}@${window.location.hostname}`;
            const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

            const icsContent = [
                'BEGIN:VCALENDAR',
                'VERSION:2.0',
                'PRODID:-//Jinja.app//Event Calendar//EN',
                'CALSCALE:GREGORIAN',
                'METHOD:PUBLISH',
                'BEGIN:VEVENT',
                `UID:${uid}`,
                `DTSTAMP:${now}`,
                `DTSTART:${start}`,
                `DTEND:${end}`,
                `SUMMARY:${title}`,
                description ? `DESCRIPTION:${description}` : '',
                location ? `LOCATION:${location}` : '',
                `URL:${window.location.href}`,
                'END:VEVENT',
                'END:VCALENDAR'
            ].filter(Boolean).join('\r\n');

            const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = (event.title || 'event').replace(/[^a-z0-9]/gi, '-').toLowerCase() + '.ics';
            link.click();

            setTimeout(() => URL.revokeObjectURL(url), 10000);
        },

        openSharePopup(url, name, width, height) {
            const left = (window.innerWidth - width) / 2;
            const top = (window.innerHeight - height) / 2;
            const features = `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`;
            window.open(url, name, features);
        },

        shareOnTwitter() {
            const url = encodeURIComponent(window.location.href);
            const text = encodeURIComponent(this.eventPayload.title || '');
            this.openSharePopup(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, 'twitter-share', 550, 420);
        },

        shareOnFacebook() {
            const url = encodeURIComponent(window.location.href);
            const description = encodeURIComponent(this.eventPayload.title || '');
            this.openSharePopup(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${description}`, 'facebook-share', 600, 400);
        },

        shareOnLinkedIn() {
            const url = encodeURIComponent(window.location.href);
            this.openSharePopup(`https://www.linkedin.com/shareArticle?mini=true&url=${url}`, 'linkedin-share', 600, 600);
        },

        shareOnPinterest() {
            const url = window.location.href;
            const description = this.eventPayload.title || '';
            const media = this.eventPayload.featured_image || '';

            let imageUrl = media;
            if (imageUrl && !imageUrl.startsWith('http')) {
                imageUrl = window.location.origin + (imageUrl.startsWith('/') ? '' : '/') + imageUrl;
            }

            const params = new URLSearchParams();
            params.append('url', url);
            params.append('description', description);
            if (imageUrl) params.append('media', imageUrl);

            this.openSharePopup(`https://www.pinterest.com/pin/create/button/?${params.toString()}`, 'pinterest-share', 750, 600);
        },

        async downloadFile(url, filename) {
            if (!url || url === '#') {
                console.error('No file URL provided for download');
                return;
            }

            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');

                link.href = blobUrl;
                link.download = filename || url.split('/').pop().split('?')[0] || 'download';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
            } catch (error) {
                console.error('Download failed, falling back to new tab:', error);
                window.open(url, '_blank');
            }
        },

        async printPDF(pdfUrl) {
            if (!pdfUrl || pdfUrl === '#') {
                console.error('No PDF URL provided');
                return;
            }

            const printWindow = window.open('about:blank', '_blank');

            if (!printWindow) {
                console.error('Popup blocked');
                alert(this.messages.allowPopupsToPrint);
                return;
            }

            document.body.style.cursor = 'wait';

            try {
                const response = await fetch(pdfUrl);
                if (!response.ok) throw new Error('Failed to fetch PDF');

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);

                printWindow.location.href = blobUrl;

                setTimeout(() => {
                    printWindow.print();
                    document.body.style.cursor = 'default';
                }, 2000);

                setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
            } catch (error) {
                console.error('Error loading PDF:', error);
                document.body.style.cursor = 'default';
                printWindow.close();
                window.open(pdfUrl, '_blank');
            }
        },

        getCopyLabel(button) {
            return button.querySelector('[data-copy-label]') || button.querySelector('span:last-of-type') || button.querySelector('span');
        },

        applyCopySuccessState(button, label, originalText, originalClasses) {
            button.className = button.className.replace(/\b(?:bg|text|hover:bg)-[^\s]+\b/g, '').replace(/\s+/g, ' ').trim();
            button.classList.add('bg-success', 'text-primary-foreground');

            if (label) label.textContent = this.messages.copied;

            setTimeout(() => {
                button.className = originalClasses;
                if (label) label.textContent = originalText;
            }, 2000);
        },

        copyToClipboard(button) {
            const targetButton = button ? button.closest('button') : null;
            if (!targetButton) return;

            const url = window.location.href;
            const label = this.getCopyLabel(targetButton);
            const originalText = label ? label.textContent : '';
            const originalClasses = targetButton.className;

            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(url).then(() => {
                    this.applyCopySuccessState(targetButton, label, originalText, originalClasses);
                }).catch((error) => {
                    console.error('Copy failed:', error);
                    this.fallbackCopy(url, label, originalText, targetButton, originalClasses);
                });
            } else {
                this.fallbackCopy(url, label, originalText, targetButton, originalClasses);
            }
        },

        fallbackCopy(url, label, originalText, button, originalClasses) {
            const textArea = document.createElement('textarea');
            textArea.value = url;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                const successful = document.execCommand('copy');
                if (successful) {
                    this.applyCopySuccessState(button, label, originalText, originalClasses);
                } else {
                    alert(`${this.messages.copyFailedManual} ${url}`);
                }
            } catch (error) {
                console.error('Fallback copy failed:', error);
                alert(`${this.messages.unableToCopyManual} ${url}`);
            }

            document.body.removeChild(textArea);
        }
    }));
});

document.addEventListener('DOMContentLoaded', function() {
    setTimeout(function() {
        document.querySelectorAll('.related-events-grid, .related-items-grid').forEach(section => {
            section.querySelectorAll('[data-aos]').forEach(el => {
                el.removeAttribute('data-aos');
                el.style.opacity = '1';
                el.style.transform = 'none';
            });
        });
    }, 100);
});
